import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { noop } from "lodash";

import { ClientConfig } from "../../../../commons/libs/externals/veloconnect-proxy";
import {
  addVeloconnectEndpointConfiguration,
  getConfiguredVeloconnectEndpoints,
  getVeloconnectEndpoint,
  getVeloconnectEndpoints,
  getVeloconnectErrorType,
  getVisibleVeloconnectEndpoints,
  isVeloconnectEndpointVisible,
  isVeloconnectImportReady,
  removeVeloconnectEndpointConfiguration
} from "../../../../commons/libs/veloconnect-service";
import useQuery from "../hooks/use-query";

interface Arguments {
  refetchDependencies?: React.DependencyList;
  onFetchSuccess?: (endpoints: ClientConfig[]) => void;
  onFetchError?: (error: Error) => void;
}

/** A hook that fetches the Veloconnect endpoint and provides utility functions to interact with them. */
const useVeloconnectEndpoints = ({
  refetchDependencies = [],
  onFetchSuccess = noop,
  onFetchError = noop
}: Arguments = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery<ClientConfig[]>({
    queryKey: ["veloconnect-endpoints", ...refetchDependencies] as const,
    queryFn: getVeloconnectEndpoints,
    onData: onFetchSuccess,
    onError: error => {
      onFetchError(error);
      console.error("Error fetching Veloconnect endpoints", error);
    }
  });

  const areReachable = query.isSuccess;

  const endpoints = query.data;

  const visibleEndpoints = useMemo(() => getVisibleVeloconnectEndpoints(endpoints ?? []), [endpoints]);

  const configuredEndpoints = useMemo(() => getConfiguredVeloconnectEndpoints(endpoints ?? []), [endpoints]);

  const areAnyEndpointsConfigured = configuredEndpoints.length > 0;

  const areImportsReady = areAnyEndpointsConfigured
    ? configuredEndpoints.every(endpoint => isVeloconnectImportReady(endpoint))
    : false;

  const endpointsWithInvalidCredentials = useMemo(
    () => configuredEndpoints.filter(endpoint => endpoint.areCredentialsInvalid),
    [configuredEndpoints]
  );

  const getEndpoint = useCallback(
    (endpointKey: string) => getVeloconnectEndpoint(endpoints ?? [], endpointKey),
    [endpoints]
  );

  const getEndpointByBrandKey = useCallback(
    (brandKey: string) => (endpoints ?? []).find(endpoint => endpoint.brandKeys.includes(brandKey)),
    [endpoints]
  );

  const isVisible = useCallback(
    (endpointKey: string) => isVeloconnectEndpointVisible(endpoints ?? [], endpointKey),
    [endpoints]
  );

  const invalidateEndpoints = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["veloconnect-endpoints"] }),
      queryClient.invalidateQueries({ queryKey: ["veloconnect-endpoint"] })
    ]);

  const addConfiguration = async (endpointKey: string, buyersId: string, password: string) => {
    const response = await addVeloconnectEndpointConfiguration(endpointKey, buyersId, password);
    void invalidateEndpoints();
    return response;
  };

  const removeConfiguration = async (endpointKey: string) => {
    const response = await removeVeloconnectEndpointConfiguration(endpointKey);
    void invalidateEndpoints();
    return response;
  };

  return {
    query,
    error: getVeloconnectErrorType(query.error),
    areReachable,
    endpoints,
    visibleEndpoints,
    configuredEndpoints,
    areAnyEndpointsConfigured,
    endpointsWithInvalidCredentials,
    areImportsReady,
    getEndpoint,
    getEndpointByBrandKey,
    addConfiguration,
    removeConfiguration,
    isVisible
  };
};

export type UseVeloconnectEndpointsReturn = ReturnType<typeof useVeloconnectEndpoints>;

export default useVeloconnectEndpoints;
